import React from "react";
import { clsx } from "clsx";

/**
 * The properties for {@link Stat}
 */
export type StatProps = {
    /** The label for the stat */
    label: string;

    /** The value to display */
    value: React.ReactNode;

    /** Item to display below the value */
    sub?: React.ReactNode;

    /**
     * If `true` (default) the stat renders as a self-contained surface card with
     * the design-token padding, radius and shadow. Pass `false` for the legacy
     * unstyled inline layout (used when something else wraps the stat).
     */
    card?: boolean;

    /** Optional leading icon (any heroicon-style svg with `data-slot="icon"`). */
    icon?: React.ReactNode;

    /** Override the default class names. */
    className?: string;
};

/**
 * A simple stat. Defaults to a card-style surface so that pages don't need to
 * repeat the `rounded-xl border bg-white p-5 dark:...` boilerplate that used to
 * surround every Stat in the dashboard.
 */
export default function Stat({ label, value, sub, icon, card = true, className }: StatProps) {
    const inner = (
        <div className={clsx("flex flex-col", !card && "mt-0")}>
            {icon && (
                <div
                    className={
                        "mb-3 inline-flex size-9 items-center justify-center rounded-(--radius-control) bg-(--color-brand-50) text-(--color-brand-600) *:data-[slot=icon]:size-5 dark:bg-(--color-brand-900)/40 dark:text-(--color-brand-300)"
                    }
                >
                    {icon}
                </div>
            )}
            <span className={"text-sm/6 font-medium text-zinc-600 dark:text-zinc-300"}>{label}</span>
            <span className={"mt-1 text-2xl/8 font-semibold text-zinc-950 dark:text-white"}>{value}</span>
            {sub && <span className={"mt-2 text-xs/5 text-zinc-500 dark:text-zinc-400"}>{sub}</span>}
        </div>
    );

    if (!card) return inner;

    return (
        <div
            className={clsx(
                className,
                "rounded-(--radius-card) bg-(--surface-card) p-5 shadow-(--shadow-card-sm) ring-1 ring-zinc-950/5 transition-shadow hover:shadow-(--shadow-card-md) dark:ring-white/10",
            )}
        >
            {inner}
        </div>
    );
}
